"use client";

import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { Button } from "~/components/ui/button";

interface RotatingStar {
  id: string;
  angle: number;
  distance: number;
  size: number;
  opacity: number;
  twinkleDuration: number;
  twinkleDelay: number;
}

interface ConstellationStar {
  id: string;
  left: number;
  top: number;
  size: number;
  duration: number;
  delay: number;
}

interface ConstellationLine {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  opacity: number;
}

const STAR_COUNT = 120;
const CONSTELLATION_STAR_COUNT = 12;
const MAX_LINE_DISTANCE = 150;
const ROTATION_DURATION_SECONDS = 180;

const nightSkyStyles = `
.night-sky-container {
  position: fixed;
  inset: 0;
  background: linear-gradient(to bottom, #0a0a1a 0%, #1a1a3a 50%, #0d0d2b 100%);
  overflow: hidden;
}

.rotating-starscape {
  position: absolute;
  inset: 0;
  animation: rotateStarscape ${ROTATION_DURATION_SECONDS}s linear infinite;
  transform-origin: center center;
  will-change: transform;
}

.star {
  border-radius: 50%;
  background: white;
  animation: twinkle var(--duration, 3s) ease-in-out var(--delay, 0s) infinite;
}

.constellation-star {
  position: absolute;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 6px 2px rgba(255, 255, 255, 0.3);
  animation: constellationPulse var(--duration, 4s) ease-in-out var(--delay, 0s) infinite;
}

.constellation-line {
  stroke: rgba(255, 255, 255, 0.15);
  stroke-width: 1;
  animation: lineGlow 4s ease-in-out infinite;
}

.not-found-content-container {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
}

.title-404 {
  font-size: clamp(6rem, 20vw, 12rem);
  font-weight: 700;
  color: transparent;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.9), rgba(150, 150, 200, 0.6));
  background-clip: text;
  -webkit-background-clip: text;
  text-shadow: 0 0 40px rgba(150, 150, 255, 0.3);
  line-height: 1;
  margin-bottom: 1rem;
  animation: floatTitle 6s ease-in-out infinite;
}

.not-found-subtitle {
  font-size: clamp(1.25rem, 3vw, 1.75rem);
  color: rgba(200, 200, 230, 0.9);
  margin-bottom: 0.75rem;
  text-align: center;
}

.not-found-description {
  font-size: clamp(0.875rem, 2vw, 1rem);
  color: rgba(150, 150, 180, 0.8);
  margin-bottom: 2rem;
  text-align: center;
  max-width: 400px;
}

.back-button {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(99, 102, 241, 0.8)) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  padding: 0.75rem 2rem !important;
  border-radius: 9999px !important;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.back-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(59, 130, 246, 0.5);
  background: linear-gradient(135deg, rgba(79, 150, 255, 0.9), rgba(119, 122, 255, 0.9)) !important;
}

@keyframes rotateStarscape {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes twinkle {
  0%, 100% { opacity: var(--base-opacity, 0.3); transform: scale(1); }
  50% { opacity: calc(var(--base-opacity, 0.3) + 0.4); transform: scale(1.2); }
}

@keyframes constellationPulse {
  0%, 100% { opacity: 0.7; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.3); }
}

@keyframes lineGlow {
  0%, 100% { stroke-opacity: 0.15; }
  50% { stroke-opacity: 0.3; }
}

@keyframes floatTitle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
`;

const generateRotatingStars = (count: number): RotatingStar[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: `star-${i}`,
    angle: Math.random() * 360,
    distance: Math.random() * 0.45 + 0.1,
    size: Math.random() * 2.5 + 0.5,
    opacity: Math.random() * 0.5 + 0.3,
    twinkleDuration: Math.random() * 3 + 2,
    twinkleDelay: Math.random() * 3,
  }));
};

const generateConstellationStars = (
  count: number,
  width: number,
  height: number
): ConstellationStar[] => {
  const centerX = width / 2;
  const centerY = height / 2;
  const spreadX = width * 0.3;
  const spreadY = height * 0.25;

  return Array.from({ length: count }, (_, i) => ({
    id: `constellation-${i}`,
    left: centerX + (Math.random() - 0.5) * spreadX,
    top: centerY + (Math.random() - 0.5) * spreadY - 50,
    size: Math.random() * 2 + 2,
    duration: Math.random() * 2 + 3,
    delay: Math.random() * 2,
  }));
};

const generateConstellationLines = (
  stars: ConstellationStar[]
): ConstellationLine[] => {
  const lines: ConstellationLine[] = [];

  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const star1 = stars[i];
      const star2 = stars[j];
      const distance = Math.sqrt(
        Math.pow(star1.left - star2.left, 2) +
          Math.pow(star1.top - star2.top, 2)
      );

      const shouldConnect = (i * 7 + j * 13) % 10 > 3;
      if (distance <= MAX_LINE_DISTANCE && shouldConnect) {
        lines.push({
          id: `line-${i}-${j}`,
          x1: star1.left + star1.size / 2,
          y1: star1.top + star1.size / 2,
          x2: star2.left + star2.size / 2,
          y2: star2.top + star2.size / 2,
          opacity: 0.15 * (1 - distance / MAX_LINE_DISTANCE),
        });
      }
    }
  }

  return lines;
};

const RotatingStarsLayer = memo(
  ({
    stars,
    centerX,
    centerY,
    maxRadius,
  }: {
    stars: RotatingStar[];
    centerX: number;
    centerY: number;
    maxRadius: number;
  }) => (
    <div className="rotating-starscape">
      {stars.map((star) => {
        const angleRad = (star.angle * Math.PI) / 180;
        const actualDistance = star.distance * maxRadius;
        const x = centerX + Math.cos(angleRad) * actualDistance;
        const y = centerY + Math.sin(angleRad) * actualDistance;

        return (
          <div
            key={star.id}
            className="star"
            style={
              {
                position: "absolute",
                left: `${x}px`,
                top: `${y}px`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                "--base-opacity": star.opacity,
                "--duration": `${star.twinkleDuration}s`,
                "--delay": `${star.twinkleDelay}s`,
              } as React.CSSProperties
            }
          />
        );
      })}
    </div>
  )
);

const ConstellationLayer = memo(
  ({
    stars,
    lines,
  }: {
    stars: ConstellationStar[];
    lines: ConstellationLine[];
  }) => (
    <>
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {lines.map((line) => (
          <line
            key={line.id}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            className="constellation-line"
            strokeOpacity={line.opacity}
          />
        ))}
      </svg>
      {stars.map((star) => (
        <div
          key={star.id}
          className="constellation-star"
          style={
            {
              left: `${star.left}px`,
              top: `${star.top}px`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              "--duration": `${star.duration}s`,
              "--delay": `${star.delay}s`,
            } as React.CSSProperties
          }
        />
      ))}
    </>
  )
);

type NotFoundProps = {
  backRoute?: string;
};

export const NotFound: FC<NotFoundProps> = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const updateDimensions = useDebouncedCallback(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, 150);

  useEffect(() => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [updateDimensions]);

  const rotatingStars = useMemo(() => generateRotatingStars(STAR_COUNT), []);

  const centerX = dimensions.width / 2;
  const centerY = dimensions.height / 2;
  const maxRadius =
    Math.sqrt(
      dimensions.width * dimensions.width +
        dimensions.height * dimensions.height
    ) / 2;

  const constellationStars = useMemo(
    () =>
      generateConstellationStars(
        CONSTELLATION_STAR_COUNT,
        dimensions.width,
        dimensions.height
      ),
    [dimensions.width, dimensions.height]
  );

  const constellationLines = useMemo(
    () => generateConstellationLines(constellationStars),
    [constellationStars]
  );

  if (dimensions.width === 0) {
    return null;
  }

  return (
    <>
      <style>{nightSkyStyles}</style>
      <div ref={containerRef} className="night-sky-container">
        <RotatingStarsLayer
          stars={rotatingStars}
          centerX={centerX}
          centerY={centerY}
          maxRadius={maxRadius}
        />
        <ConstellationLayer
          stars={constellationStars}
          lines={constellationLines}
        />

        <div className="not-found-content-container">
          <h1 className="title-404">
            <FormattedMessage id="notFound.title" defaultMessage="404" />
          </h1>
          <p className="not-found-subtitle">
            <FormattedMessage
              id="notFound.heading"
              defaultMessage="Page not found"
            />
          </p>
          <p className="not-found-description">
            <FormattedMessage
              id="notFound.description"
              defaultMessage="The page you're looking for doesn't exist or you don't have access to it."
            />
          </p>
          <Button onClick={() => navigate("/")} className="back-button">
            <FormattedMessage
              id="notFound.backButton"
              defaultMessage="Go home"
            />
          </Button>
        </div>
      </div>
    </>
  );
};
